/**
 * A question's scene: a setup of a bench that reproduces the question, solved
 * by that bench's own engine and checked against the question's answer key.
 *
 * See docs/FeatureDesigns/question-scenes/question-scenes-target.md for the design.
 *
 * Everything here is plain data that serialises to JSON: a scene is written by
 * a model, stored in Cosmos and opened by a browser, and none of those share
 * anything but the JSON.
 */

export type BenchId = 'mechanics' | 'optics';

export interface Scene {
    bench: BenchId;
    /** Of that bench's setup vocabulary. */
    specVersion: number;
    /** The bench's own vocabulary — see its manifest. Validated by the bench. */
    setup: Record<string, unknown>;
    /** What the question wants, one entry per thing asked. */
    asks: Ask[];
    /** When a given is what is asked for: the engine runs forwards, so a
     *  question that runs backwards says which parameter to search. */
    inverse?: Inverse;
    /** "g = 10 m/s²", "pulleys are frictionless" — what the question assumes. */
    assumptions: string[];
}

export interface Ask {
    /** 'a', 'b'… for a structured question. */
    part?: string;
    /** A readout the bench declares, or — for an inverse scene — the `param`. */
    readout: string;
    /** Which element, when the bench has several of it: "2" for the second string. */
    on?: string;
}

export interface Inverse {
    /** A dotted path into `setup`: 'load.mass', 'object.distance', 'lens.f'. */
    param: string;
    /** The readout the question gives… */
    readout: string;
    /** …and its value, in that readout's unit. */
    equals: number;
    /** Where to search, in the parameter's own unit. */
    range: [number, number];
}

// ── What the compiler returns ───────────────────────────────────────────────

export type Confidence = 'high' | 'medium' | 'low';

export type CannotReason =
    /** Definitions, "explain why" — nothing to set up. */
    | 'not-a-setup'
    /** A lever on the pulley bench; a mirror on the lens-only vocabulary. */
    | 'outside-bench'
    /** Pulley friction stated as an efficiency; an object at infinity. */
    | 'needs-unsupported-feature'
    /** The text and the figure do not fix the arrangement. */
    | 'setup-ambiguous'
    /** The givens are in a figure that cannot be read. */
    | 'diagram-unreadable';

export const CANNOT_REASONS: readonly CannotReason[] = [
    'not-a-setup', 'outside-bench', 'needs-unsupported-feature', 'setup-ambiguous', 'diagram-unreadable',
];

export type CompileVerdict =
    | {
        outcome: 'scene';
        scene: Scene;
        /** The answer key, read into readout terms — one entry per ask it answers. */
        expected: Expected[];
        /** The compiler's own statement. Never the trust label: that is earned by the checks. */
        confidence: Confidence;
        /** What it was unsure about. */
        confidenceWhy: string;
        fidelity: 'exact' | 'approximate';
        /** Where the bench differs from the book. */
        approximations?: string[];
    }
    | {
        outcome: 'cannot';
        reason: CannotReason;
        /** One or two lines a reviewer can check. */
        why: string;
    };

export interface Expected {
    /** Index into `scene.asks`. */
    ask: number;
    value?: number;
    unit?: string;
    /** The key's number as written — "37.5", "0.40" — so the comparison can
     *  honour the precision it was stated to. */
    stated?: string;
    category?: string;
    /** The key has no answer for this ask. */
    absent?: true;
}

// ── What a bench declares ───────────────────────────────────────────────────

export interface ReadoutDecl {
    key: string;
    label: string;
    /** Absent for a category. */
    unit?: string;
    categories?: string[];
    /** Accepts `on`, and what it names. */
    on?: string;
    tolerance?: { relative?: number; absolute?: number };
}

export type Reading = number | string | null;

export interface BenchSolution {
    /** Every readout the bench could compute for this setup. */
    readings: Record<string, Reading>;
    /** Readouts that take `on`, by `on` value. */
    indexed?: Record<string, Record<string, Reading>>;
    /** The bench's own account of its working, where it has one. */
    working?: string[];
    /** What the engine found wrong. Any entry means the scene does not stand. */
    problems: string[];
}

export interface BenchManifest<Setup = Record<string, unknown>, W = unknown> {
    bench: BenchId;
    title: string;
    specVersion: number;
    /** Module ids of the chapters this bench serves. The question's module picks the bench. */
    covers: string[];
    /** Solved exactly, or predicted. */
    exact: boolean;
    /** The setup vocabulary, as prompt text, with examples. */
    vocabulary: string;
    readouts: ReadoutDecl[];
    /** What the engine always assumes. */
    assumptions: string[];
    /**
     * Conventions a question may leave unstated — g, above all. A setup that
     * leaves one out is built with `default`; when the key then disagrees, the
     * check tries each alternative, because "the key used g = 10 and never said
     * so" is a fact about the key's convention, not a wrong scene. The ENGINE
     * decides which convention the key matches; the model never guesses it.
     */
    unstated?: { path: string; label: string; default: number; alternatives: number[] }[];
    /** Precise, fixable messages; empty when the setup is good. */
    validate(setup: unknown): string[];
    build(setup: Setup): W;
    solve(setup: Setup): BenchSolution;
}

// ── What the checks produce ─────────────────────────────────────────────────

export interface AskAnswer {
    ask: Ask;
    value: Reading;
    unit?: string;
}

export interface SceneSolution {
    ok: boolean;
    /** When `ok` is false: why, precisely enough to repair. */
    errors: string[];
    answers: AskAnswer[];
    readings: Record<string, Reading>;
    working?: string[];
    /** For an inverse scene: the parameter found. */
    found?: number;
}

export type Agreement = 'agrees' | 'disagrees' | 'no-key';

export type Trust = 'curated' | 'verified' | 'unverified' | 'rejected';

export interface SceneCheck {
    solution: SceneSolution;
    /** One per ask. */
    agreement: Agreement[];
    /** One per ask: what the comparison saw, for a reviewer. */
    notes: string[];
    trust: Exclude<Trust, 'curated'>;
    /** Unstated conventions the key turned out to use: `{ g: 10 }`. The scene
     *  is opened with them, so the bench shows the numbers that were checked. */
    assumed?: Record<string, number>;
}
